import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Grid, Skeleton, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined, CalendarMonthOutlined, LinkOutlined, PlayArrowRounded, SavingsOutlined, ShowChartOutlined,
  StopCircleOutlined, SyncOutlined, TrendingDownOutlined, TrendingUpOutlined,
} from '@mui/icons-material';
import { EmptyRow, Kpi, PageHeader, Panel, Pnl, inr, num } from '../components/ui';
import { errorMessage, kiteApi, tradingApi } from '../services/api';
import { TradingStatus } from '../types';
import { t } from '../utils/theme';

const POLL_MS = 30000;
const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TradingStatus | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [stopOpen, setStopOpen] = useState(false);
  const [busy, setBusy] = useState<'' | 'stop' | 'resume' | 'rebalance'>('');

  const refresh = useCallback(async () => {
    try {
      setStatus(await tradingApi.status());
    } catch (err) {
      setSnack({ msg: errorMessage(err, 'Could not load status'), severity: 'error' });
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const result = new URLSearchParams(location.search).get('kite');
    if (result === 'ok') setSnack({ msg: 'Kite login successful', severity: 'success' });
    if (result === 'error') setSnack({ msg: 'Kite login failed. Try again.', severity: 'error' });
    if (result) navigate('/dashboard', { replace: true });
  }, [location.search, navigate]);

  const kiteLogin = async () => {
    try {
      const { login_url } = await kiteApi.loginUrl();
      window.location.href = login_url;
    } catch (err) {
      setSnack({ msg: errorMessage(err, 'Could not get Kite login URL'), severity: 'error' });
    }
  };

  const run = async (kind: 'stop' | 'resume' | 'rebalance', fn: () => Promise<string>) => {
    setBusy(kind);
    try {
      setSnack({ msg: await fn(), severity: 'info' });
    } catch (err) {
      setSnack({ msg: errorMessage(err), severity: 'error' });
    } finally {
      setBusy('');
      refresh();
    }
  };
  const emergencyStop = () => {
    setStopOpen(false);
    run('stop', async () => `Sold ${(await tradingApi.emergencyStop()).closed} holding(s). Strategy switched off.`);
  };
  const resume = () => run('resume', async () => { await tradingApi.resume(); return 'Strategy enabled'; });
  const rebalance = () => run('rebalance', async () => {
    const r = await tradingApi.rebalanceNow();
    return `Rebalanced: ${r.note}`;
  });

  if (!status) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2, 3].map((i) => <Grid item xs={12} sm={6} lg={3} key={i}><Skeleton variant="rounded" height={130} /></Grid>)}
        <Grid item xs={12}><Skeleton variant="rounded" height={300} /></Grid>
      </Grid>
    );
  }

  const live = status.mode === 'LIVE';
  const equity = Number(status.equity), capital = Number(status.capital);
  const unrealized = status.holdings.reduce((s, h) => s + Number(h.unrealized), 0);
  const m = status.market;
  const rb = status.rebalance;
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Box>
      <PageHeader
        title="Portfolio"
        subtitle={today}
        actions={
          <>
            <Chip label={live ? 'LIVE · real money' : 'PAPER · demo'} color={live ? 'error' : 'default'} variant={live ? 'filled' : 'outlined'} />
            <Chip label={status.enabled ? 'Strategy on' : 'Strategy off'} color={status.enabled ? 'success' : 'default'} variant="outlined" />
            {!status.enabled && <Button variant="outlined" startIcon={<PlayArrowRounded />} onClick={resume} disabled={!!busy}>Enable</Button>}
            <Button variant="contained" color="error" startIcon={<StopCircleOutlined />} onClick={() => setStopOpen(true)} disabled={!!busy}>
              Emergency stop
            </Button>
          </>
        }
      />

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {!status.enabled && (
          <Alert severity="warning" icon={<StopCircleOutlined />}
                 action={<Button color="inherit" size="small" variant="outlined" onClick={resume} disabled={!!busy}>Enable</Button>}>
            Strategy is off: nothing is bought or sold, in {live ? 'LIVE' : 'PAPER'} mode too. Press Enable to start.
          </Alert>
        )}
        {!status.kite_configured && (
          <Alert severity="info" icon={<LinkOutlined />}
                 action={<Button color="inherit" size="small" onClick={() => navigate('/settings')}>Open settings</Button>}>
            Connect your Zerodha account: add your Kite API key and secret in Settings.
          </Alert>
        )}
        {status.kite_configured && !status.kite_logged_in && (
          <Alert severity={rb.due && status.enabled ? 'warning' : 'info'} icon={<LinkOutlined />}
                 action={<Button color="inherit" size="small" variant="outlined" onClick={kiteLogin}>Login to Kite</Button>}>
            {rb.due && status.enabled
              ? 'Rebalance due this month. Log in to Kite and it runs automatically after 09:20 on a trading day, or press Rebalance now.'
              : `Kite: not logged in today. Not needed until the next rebalance (${fmtDate(rb.next)}); log in only for ${status.holdings.length ? 'live prices' : 'a backtest'}.`}
          </Alert>
        )}
        {status.kite_logged_in && rb.due && status.enabled && (
          <Alert severity="warning" icon={<SyncOutlined />}
                 action={<Button color="inherit" size="small" variant="outlined" onClick={rebalance} disabled={!!busy}>
                   {busy === 'rebalance' ? <CircularProgress size={16} color="inherit" /> : 'Rebalance now'}
                 </Button>}>
            Rebalance due. The trader runs it after 09:20 on a trading day; you can also run it now.
          </Alert>
        )}
        {status.kite_logged_in && !rb.due && (
          <Alert severity="success" icon={<LinkOutlined />}>Connected to Kite as <b>{status.kite_user_id}</b> for today.</Alert>
        )}
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Kpi label="Portfolio value" icon={<AccountBalanceWalletOutlined fontSize="small" />} value={inr(equity)}
               sub={<>Started with {inr(capital)} · <Pnl value={equity - capital} /></>} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Kpi label="Holdings" icon={<ShowChartOutlined fontSize="small" />} value={`${status.holdings.length} stocks`}
               sub={<>Worth {inr(status.holdings_value)} · unrealised <Pnl value={unrealized} /></>} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Kpi label="Cash" icon={<SavingsOutlined fontSize="small" />} value={inr(status.cash)}
               sub={<>Realised so far <Pnl value={status.realized_pnl} /></>} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Kpi label="Next rebalance" icon={<CalendarMonthOutlined fontSize="small" />}
               value={rb.due ? 'Due now' : fmtDate(rb.next)} sub={rb.last ? `Last: ${fmtDate(rb.last)}` : 'Not rebalanced yet'} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Panel title="Market filter" subtitle={`NIFTY 50 vs its ${m.sma_days}-day average`}>
            {!m.enabled ? (
              <Typography sx={{ color: t.text2 }}>Switched off in Settings: the strategy stays invested in all markets.</Typography>
            ) : m.on == null ? (
              <Typography sx={{ color: t.text3 }}>No index data yet. It downloads on the first rebalance or backtest.</Typography>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1.25, borderRadius: 2, display: 'flex', bgcolor: m.on ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                           color: m.on ? t.profit : t.loss }}>
                  {m.on ? <TrendingUpOutlined /> : <TrendingDownOutlined />}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{m.on ? 'Risk on: invested' : 'Risk off: holding cash'}</Typography>
                  <Typography variant="body2" sx={{ color: t.text2 }}>
                    NIFTY 50 {num(m.index_close)} vs average {num(m.index_sma)} · as of {fmtDate(m.asof)}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} md={7}>
          <Panel title="Last rebalance" subtitle={rb.last_log ? `${fmtDate(rb.last_log.date)} · ${rb.last_log.note}` : 'Runs on the first trading day of each month'}>
            {rb.last_log ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="overline" sx={{ color: t.text3 }}>Bought</Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                    {rb.last_log.bought.map((b) => <Chip key={b.symbol} size="small" label={`${b.symbol.replace('NSE:', '')} #${b.rank}`} sx={{ color: t.profit }} />)}
                    {!rb.last_log.bought.length && <Typography variant="body2" sx={{ color: t.text3 }}>none</Typography>}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="overline" sx={{ color: t.text3 }}>Sold</Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                    {rb.last_log.sold.map((s) => <Chip key={s.symbol} size="small" label={`${s.symbol.replace('NSE:', '')} · ${s.reason.toLowerCase().replace('_', ' ')}`} sx={{ color: t.loss }} />)}
                    {!rb.last_log.sold.length && <Typography variant="body2" sx={{ color: t.text3 }}>none</Typography>}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="overline" sx={{ color: t.text3 }}>Kept</Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                    {rb.last_log.held.map((s) => <Chip key={s} size="small" variant="outlined" label={s.replace('NSE:', '')} />)}
                    {!rb.last_log.held.length && <Typography variant="body2" sx={{ color: t.text3 }}>none</Typography>}
                  </Stack>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ color: t.text2 }}>
                On the rebalance day the strategy ranks the universe, sells holdings that dropped out, and buys the new leaders.
              </Typography>
            )}
          </Panel>
        </Grid>
      </Grid>

      <Panel title="Holdings" pad={false}
             subtitle={status.holdings.length
               ? `Prices ${status.prices_source === 'live' ? 'live from Kite' : `as of ${fmtDate(status.prices_asof)} (log in to Kite to refresh)`}`
               : undefined}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stock</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Avg buy</TableCell>
                <TableCell align="right">Price</TableCell><TableCell align="right">Value</TableCell>
                <TableCell align="right">P&L</TableCell><TableCell align="right">Change</TableCell><TableCell>Since</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status.holdings.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{h.symbol.replace('NSE:', '')} <span style={{ color: t.text3, fontWeight: 400 }}>#{h.entry_rank ?? '—'}</span></TableCell>
                  <TableCell align="right">{h.qty}</TableCell>
                  <TableCell align="right">{num(h.entry_price)}</TableCell>
                  <TableCell align="right">{num(h.price)}</TableCell>
                  <TableCell align="right">{num(h.value)}</TableCell>
                  <TableCell align="right"><Pnl value={h.unrealized} /></TableCell>
                  <TableCell align="right" sx={{ color: h.change_pct >= 0 ? t.profit : t.loss }}>{h.change_pct >= 0 ? '+' : ''}{h.change_pct.toFixed(1)}%</TableCell>
                  <TableCell sx={{ color: t.text2 }}>{fmtDate(h.entry_time)}</TableCell>
                </TableRow>
              ))}
              {!status.holdings.length && (
                <EmptyRow cols={8} text={status.enabled ? 'No holdings yet. They are bought at the next rebalance.' : 'No holdings. Enable the strategy to start.'} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Dialog open={stopOpen} onClose={() => setStopOpen(false)} aria-labelledby="stop-title">
        <DialogTitle id="stop-title">Emergency stop?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sells every holding at market price and switches the strategy off. Needs today's Kite login.
            {live && <><br /><b>You are in LIVE mode: real sell orders will be sent to Zerodha.</b></>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStopOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={emergencyStop}>Sell everything</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack?.severity ?? 'info'} onClose={() => setSnack(null)} variant="filled">{snack?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
